'use client'

import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import type ReactMarkdown from 'react-markdown'
import { useTranslations } from 'next-intl'
import type { DropzoneState, FileRejection } from 'react-dropzone'
import { useDropzone } from 'react-dropzone'
import { Upload, FileText, Eye, Maximize2, Download } from 'lucide-react'
import type { DocumentExportSettings } from '@/lib/document-export-settings'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { MarkdownFenceNotice } from '@/components/markdown-fence-notice'
import { cn } from '@/lib/utils'
import {
  MarkdownEditorPreview,
  MarkdownPreviewStatus,
  useMarkdownPreview,
} from '@/components/markdown-editor-preview'
import {
  markdownPreviewRehypePlugins,
  markdownPreviewRemarkPlugins,
} from '@/lib/markdown/preview-plugins'

type UploadState = {
  error: 'tooLarge' | 'invalidType' | 'tooMany' | 'readFailed' | null
  reading: boolean
  maxSizeBytes: number
  file: { name: string; size: number } | null
}

type HeadingConfig = {
  label: React.ReactNode
  icon: React.ReactNode
  className?: string
}

type UploadCopy = {
  subtitle?: React.ReactNode
  dragActive: React.ReactNode
  dragInactive: React.ReactNode
  fileConstraints: React.ReactNode
}

type DropzoneLike = Pick<
  DropzoneState,
  'getRootProps' | 'getInputProps' | 'isDragActive'
>

export type MarkdownConverterShellProps = {
  markdownContent: string
  exampleMarkdown: string
  onMarkdownChange: (value: string) => void
  onLoadExample: () => void
  dropzone: DropzoneLike
  uploadState: UploadState
  uploadAriaLabel: string
  uploadCopy: UploadCopy
  headings: {
    upload: HeadingConfig
    editor: HeadingConfig
    preview: HeadingConfig
  }
  loadExampleLabel: React.ReactNode
  lineCountLabel?: React.ReactNode
  uploadDropIcon?: React.ReactNode
  previewRemarkPlugins?: React.ComponentProps<
    typeof ReactMarkdown
  >['remarkPlugins']
  previewRehypePlugins?: React.ComponentProps<
    typeof ReactMarkdown
  >['rehypePlugins']
  previewPlaceholder: React.ReactNode
  renderedHtml?: string
  renderingHtml?: boolean
  onEnterFullscreen: () => void
  onConvert: () => void
  isConverting: boolean
  disableConvert?: boolean
  convertLabels: {
    idle: React.ReactNode
    busy: React.ReactNode
  }
  fullscreenViewer: React.ReactNode
  sourceScrollRef: React.RefObject<HTMLTextAreaElement>
  previewScrollRef: React.RefObject<HTMLDivElement>
  onSourceScroll: (event: React.UIEvent<HTMLTextAreaElement>) => void
  previewClassName?: string
  previewToolbarActions?: React.ReactNode
  previewExportSettings?: DocumentExportSettings
  convertButtonClassName?: string
  convertIcon?: React.ReactNode
  verificationWidget?: React.ReactNode
  actionHelperText?: React.ReactNode
}

type MarkdownConverterCommonOptions = {
  useTransition?: boolean
  maxFileSizeBytes?: number
}

export const useMarkdownConverterCommon = (
  options: MarkdownConverterCommonOptions = {},
) => {
  const [markdownContent, setMarkdownContentInternal] = useState('')
  const [fileName, setFileName] = useState('')
  const [isConverting, setIsConverting] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const maxSizeBytes = options.maxFileSizeBytes ?? 10 * 1024 * 1024
  const [uploadState, setUploadState] = useState<UploadState>({
    error: null,
    reading: false,
    maxSizeBytes,
    file: null,
  })
  const readerRef = useRef<FileReader | null>(null)
  const cancelRead = useCallback(() => {
    const reader = readerRef.current
    readerRef.current = null
    if (reader?.readyState === FileReader.LOADING) reader.abort()
  }, [])
  useEffect(() => cancelRead, [cancelRead])

  const setMarkdownContent = useCallback(
    (value: string) => {
      // 手动编辑优先，防止尚未完成的文件读取覆盖新输入。
      cancelRead()
      setUploadState((state) => ({ ...state, reading: false }))
      setMarkdownContentInternal(value)
    },
    [cancelRead],
  )

  const sourceScrollRef = useRef<HTMLTextAreaElement | null>(null)
  const previewScrollRef = useRef<HTMLDivElement | null>(null)
  const isScrollSyncingRef = useRef(false)

  const syncScroll = useCallback((source: HTMLElement, target: HTMLElement) => {
    if (isScrollSyncingRef.current) return
    isScrollSyncingRef.current = true

    const sourceScrollHeight =
      source.scrollHeight - source.clientHeight || source.scrollHeight
    const targetScrollHeight =
      target.scrollHeight - target.clientHeight || target.scrollHeight

    const ratio =
      sourceScrollHeight > 0
        ? source.scrollTop / Math.max(sourceScrollHeight, 1)
        : 0
    target.scrollTop = ratio * targetScrollHeight

    requestAnimationFrame(() => {
      isScrollSyncingRef.current = false
    })
  }, [])

  const handleSourceScroll = useCallback(
    (event: React.UIEvent<HTMLTextAreaElement>) => {
      if (!previewScrollRef.current) return
      syncScroll(event.currentTarget, previewScrollRef.current)
    },
    [syncScroll],
  )

  const onDrop = useCallback(
    (acceptedFiles: File[], rejections: FileRejection[]) => {
      cancelRead()
      if (rejections.length) {
        const codes = rejections.flatMap((item) =>
          item.errors.map((error) => error.code),
        )
        const error = codes.includes('too-many-files')
          ? 'tooMany'
          : codes.includes('file-too-large')
            ? 'tooLarge'
            : 'invalidType'
        setUploadState((state) => ({ ...state, reading: false, error }))
        return
      }
      const file = acceptedFiles[0]
      if (!file) {
        setUploadState((state) => ({ ...state, reading: false }))
        return
      }
      setUploadState((state) => ({ ...state, reading: true, error: null }))
      const reader = new FileReader()
      readerRef.current = reader
      const fail = () => {
        if (readerRef.current !== reader) return
        readerRef.current = null
        setUploadState((state) => ({
          ...state,
          reading: false,
          error: 'readFailed',
        }))
      }
      reader.onload = () => {
        if (readerRef.current !== reader) return
        if (typeof reader.result !== 'string') {
          fail()
          return
        }
        readerRef.current = null
        setMarkdownContentInternal(reader.result)
        setFileName(file.name)
        setUploadState({
          error: null,
          reading: false,
          maxSizeBytes,
          file: { name: file.name, size: file.size },
        })
      }
      reader.onerror = fail
      reader.onabort = fail
      try {
        reader.readAsText(file)
      } catch {
        fail()
      }
    },
    [cancelRead, maxSizeBytes],
  )

  const dropzone = useDropzone({
    onDrop,
    accept: {
      'text/markdown': ['.md', '.markdown'],
      'text/plain': ['.txt'],
    },
    // 同时校验扩展名，避免错误的 text/plain MIME 放行其他格式。
    validator: (file) =>
      /\.(md|markdown|txt)$/i.test(file.name)
        ? null
        : { code: 'file-invalid-type', message: 'Unsupported file extension' },
    maxFiles: 1,
    maxSize: maxSizeBytes,
  })

  const lineCount = useMemo(
    () => markdownContent.split('\n').length,
    [markdownContent],
  )

  const enterFullscreen = useCallback(() => setIsFullscreen(true), [])
  const exitFullscreen = useCallback(() => setIsFullscreen(false), [])

  return {
    markdownContent,
    setMarkdownContent,
    fileName,
    setFileName,
    isConverting,
    setIsConverting,
    isFullscreen,
    enterFullscreen,
    exitFullscreen,
    dropzone,
    uploadState,
    lineCount,
    sourceScrollRef,
    previewScrollRef,
    handleSourceScroll,
  }
}

export function MarkdownConverterShell({
  markdownContent,
  exampleMarkdown,
  onMarkdownChange,
  onLoadExample,
  dropzone,
  uploadState,
  uploadAriaLabel,
  uploadCopy,
  headings,
  loadExampleLabel,
  lineCountLabel = 'lines',
  uploadDropIcon = (
    <Upload className="mx-auto mb-2 text-muted-foreground" size={36} />
  ),
  previewRemarkPlugins,
  previewPlaceholder,
  onEnterFullscreen,
  onConvert,
  isConverting,
  disableConvert = false,
  convertLabels,
  fullscreenViewer,
  sourceScrollRef,
  previewScrollRef,
  onSourceScroll,
  previewRehypePlugins,
  previewClassName = 'prose prose-sm max-w-none dark:prose-invert',
  previewToolbarActions,
  previewExportSettings,
  convertButtonClassName = 'flex items-center px-6 py-3 text-lg',
  convertIcon = <Download className="mr-2" size={24} />,
  renderedHtml,
  renderingHtml,
  verificationWidget,
  actionHelperText,
}: MarkdownConverterShellProps) {
  const t = useTranslations('MarkdownEditorShared')
  const { getRootProps, getInputProps, isDragActive } = dropzone
  const remarkPlugins = previewRemarkPlugins ?? markdownPreviewRemarkPlugins
  const rehypePlugins = previewRehypePlugins ?? markdownPreviewRehypePlugins
  const lineCount = useMemo(
    () => markdownContent.split('\n').length,
    [markdownContent],
  )
  const isConvertDisabled =
    isConverting || !markdownContent.trim() || disableConvert
  const hasRenderedHtml = typeof renderedHtml === 'string'
  const preview = useMarkdownPreview(markdownContent, !hasRenderedHtml)
  const previewClassNames = cn('markdown-preview', previewClassName)
  const previewDirection = previewExportSettings?.textDirection ?? 'auto'
  const previewFontSize = previewExportSettings
    ? `${previewExportSettings.fontSizePt}pt`
    : undefined

  return (
    <div className="container mx-auto px-4 pb-8">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <Card>
          <CardContent className="p-6">
            <h2
              className={cn(
                'mb-4 flex items-center',
                headings.upload.className ??
                  'text-base font-semibold sm:text-lg',
              )}
            >
              {headings.upload.icon ?? <FileText className="mr-2 h-5 w-5" />}
              {headings.upload.label}
            </h2>

            <div
              {...getRootProps({
                role: 'button',
                'aria-label': uploadAriaLabel,
              })}
              className={cn(
                'cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition-colors',
                isDragActive
                  ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-border hover:border-foreground/40 dark:hover:border-foreground/60',
              )}
            >
              <input {...getInputProps({ 'aria-label': uploadAriaLabel })} />
              {uploadDropIcon ?? (
                <Upload
                  className="mx-auto mb-2 text-muted-foreground"
                  size={36}
                />
              )}
              {isDragActive ? (
                <p className="text-blue-600 dark:text-blue-400">
                  {uploadCopy.dragActive}
                </p>
              ) : (
                <div>
                  {uploadCopy.subtitle ? (
                    <h3 className="text-md mb-2 items-center font-bold text-muted-foreground">
                      {uploadCopy.subtitle}
                    </h3>
                  ) : null}
                  <p className="text-sm text-muted-foreground">
                    {uploadCopy.dragInactive}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {uploadCopy.fileConstraints}
                  </p>
                </div>
              )}
            </div>
            <div
              className="mt-3 space-y-1 break-words text-sm"
              aria-live="polite"
            >
              {uploadState.reading && <p>{t('readingFile')}</p>}
              {uploadState.error && (
                <p role="alert" className="text-red-600 dark:text-red-400">
                  {t(uploadState.error, {
                    size: uploadState.maxSizeBytes / 1024 / 1024,
                  })}{' '}
                  {t('contentKept')}
                </p>
              )}
              {uploadState.file && (
                <p className="text-muted-foreground">
                  {t('loadedFile', {
                    name: uploadState.file.name,
                    size: `${(uploadState.file.size / 1024).toFixed(1)} KB`,
                  })}
                </p>
              )}
              <p className="text-muted-foreground">
                {t('characterCount', { count: markdownContent.length })}
              </p>
            </div>
          </CardContent>

          <CardContent className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3
                className={cn(
                  'flex items-center gap-2',
                  headings.editor.className ??
                    'text-base font-semibold sm:text-lg',
                )}
              >
                {headings.editor.icon ?? (
                  <FileText className="h-5 w-5" aria-hidden />
                )}
                {headings.editor.label}
              </h3>
              <div className="flex items-center gap-2">
                <span className="hidden text-sm text-muted-foreground sm:block">
                  {lineCount} {lineCountLabel}
                </span>
                <Button onClick={onLoadExample} variant="outline" size="sm">
                  {loadExampleLabel}
                </Button>
              </div>
            </div>
            <MarkdownFenceNotice
              markdown={markdownContent}
              onApply={(content) => {
                onMarkdownChange(content)
                requestAnimationFrame(() => sourceScrollRef.current?.focus())
              }}
            />
            <textarea
              ref={sourceScrollRef}
              dir={previewDirection}
              value={markdownContent}
              onChange={(event) => onMarkdownChange(event.target.value)}
              onScroll={onSourceScroll}
              placeholder={exampleMarkdown}
              className="h-[300px] w-full resize-none rounded-lg border border-border bg-muted p-4 font-mono text-sm text-foreground focus:border-transparent focus:ring-2 focus:ring-blue-500"
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3
                className={cn(
                  'flex items-center gap-2',
                  headings.preview.className ??
                    'text-base font-semibold sm:text-lg',
                )}
              >
                {headings.preview.icon ?? <Eye className="h-5 w-5" />}
                {headings.preview.label}
              </h3>
              <div className="flex items-center gap-1">
                {!hasRenderedHtml && (
                  <MarkdownPreviewStatus pending={preview.pending} />
                )}
                {previewToolbarActions}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={onEnterFullscreen}
                  aria-label="Open fullscreen preview"
                >
                  <Maximize2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            </div>

            <div
              ref={previewScrollRef}
              className="max-h-[542px] min-h-[300px] overflow-y-auto rounded-lg border border-border bg-card p-4 lg:h-[542px]"
            >
              {markdownContent ? (
                <>
                  {hasRenderedHtml ? (
                    <div
                      dir={previewDirection}
                      style={{ fontSize: previewFontSize }}
                      className={previewClassNames}
                      dangerouslySetInnerHTML={{
                        __html: renderedHtml || '',
                      }}
                    />
                  ) : (
                    <div
                      dir={previewDirection}
                      style={{ fontSize: previewFontSize }}
                      className={previewClassNames}
                    >
                      <MarkdownEditorPreview
                        markdown={preview.previewMarkdown}
                        remarkPlugins={remarkPlugins}
                        rehypePlugins={rehypePlugins}
                      />
                    </div>
                  )}
                  {renderingHtml ? (
                    <div className="mt-2 text-xs text-muted-foreground">
                      Rendering preview…
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  {previewPlaceholder}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 flex flex-col items-center gap-4">
        {verificationWidget}
        {actionHelperText}
        <Button
          onClick={onConvert}
          disabled={isConvertDisabled}
          className={convertButtonClassName}
          size="lg"
        >
          {convertIcon}
          {isConverting ? convertLabels.busy : convertLabels.idle}
        </Button>
      </div>

      {fullscreenViewer}
    </div>
  )
}
